import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, COMPANY_LOGO } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, ShieldAlert, CheckCircle2, ArrowLeft, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [check, setCheck] = useState(null); // {valid, email, reason}
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setCheck({ valid: false, reason: "not-found" }); return; }
    (async () => {
      try { const { data } = await api.get(`/auth/reset-token/${token}`); setCheck(data); }
      catch { setCheck({ valid: false, reason: "error" }); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) return toast.error("Şifre en az 8 karakter olmalı");
    if (nw !== nw2) return toast.error("Şifreler eşleşmiyor");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: nw });
      setDone(true);
      setTimeout(() => nav("/login"), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Şifre sıfırlanamadı");
    } finally { setBusy(false); }
  };

  const reasonText = {
    "not-found":    "Bu bağlantı geçersiz veya hatalı.",
    "used":         "Bu bağlantı daha önce kullanılmış.",
    "invalidated":  "Bu bağlantı iptal edilmiş — daha yeni bir talep oluşturulmuş olabilir.",
    "expired":      "Bağlantının süresi dolmuş (24 saat).",
    "error":        "Bağlantı doğrulanamadı.",
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 dark:bg-slate-950 p-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <img src={COMPANY_LOGO} alt="" className="w-10 h-10 object-contain" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Hesap Kurtarma</div>
            <div className="font-display text-xl font-extrabold text-slate-900 dark:text-slate-100">Yeni Şifre Belirle</div>
          </div>
        </div>

        {check === null && (
          <div className="text-center py-8 text-sm text-slate-500">Bağlantı doğrulanıyor...</div>
        )}

        {check && !check.valid && (
          <div className="text-center py-4" data-testid="rp-invalid">
            <div className="w-16 h-16 mx-auto rounded-full bg-rose-100 grid place-items-center mb-4">
              <ShieldAlert className="w-8 h-8 text-rose-600" />
            </div>
            <div className="font-display text-lg font-extrabold text-slate-900 dark:text-slate-100 mb-2">Bağlantı Kullanılamıyor</div>
            <p className="text-sm text-slate-600 dark:text-slate-300">{reasonText[check.reason] || "Bağlantı kullanılamıyor."}</p>
            <p className="text-xs text-slate-500 mt-3">Yöneticinizden yeni bir sıfırlama bağlantısı isteyin.</p>
          </div>
        )}

        {check && check.valid && done && (
          <div className="text-center py-4" data-testid="rp-done">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 grid place-items-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <div className="font-display text-lg font-extrabold text-slate-900 dark:text-slate-100 mb-2">Şifreniz Güncellendi</div>
            <p className="text-sm text-slate-600 dark:text-slate-300">Girişe yönlendiriliyorsunuz...</p>
          </div>
        )}

        {check && check.valid && !done && (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">
              Hesap: <span className="font-mono-num font-bold">{check.email}</span> için yeni şifrenizi belirleyin.
            </p>
            <form onSubmit={submit} className="space-y-3">
              <PwField label="Yeni Şifre (min. 8 karakter)" value={nw} onChange={setNw}
                       type={show ? "text" : "password"} testId="rp-new" onToggle={() => setShow(!show)} show={show} />
              <PwField label="Yeni Şifre (tekrar)" value={nw2} onChange={setNw2}
                       type={show ? "text" : "password"} testId="rp-new2" />
              <button type="submit" disabled={busy} data-testid="rp-submit"
                className="w-full py-3 rounded-lg bg-[#1E2A9C] text-white font-bold hover:bg-[#121A68] disabled:opacity-60 transition">
                {busy ? "Kaydediliyor..." : "Şifreyi Belirle"}
              </button>
            </form>
          </>
        )}

        <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800 text-center">
          <Link to="/login" data-testid="link-back-login"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#1E2A9C] font-bold">
            <ArrowLeft className="w-3 h-3" /> Girişe Dön
          </Link>
        </div>
      </div>
    </div>
  );
}

function PwField({ label, value, onChange, type, testId, onToggle, show }) {
  return (
    <div>
      <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">{label}</label>
      <div className="relative">
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
          required autoComplete="new-password"
          className="w-full mt-1 px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-[#1E2A9C]/30 outline-none" />
        {onToggle && (
          <button type="button" onClick={onToggle} data-testid="rp-toggle"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
}
